import React from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import type { HealthStatus } from './types';
import { formatEnv, formatWhen } from './formatHealth';
import { LivePulse, StatusPill } from './StatusBits';

export const HealthPageHeader: React.FC<{
  title: string;
  subtitle?: string;
  status: HealthStatus;
  reason: string;
  environment: string;
  region: string;
  instanceId?: string;
  generatedAt?: string;
  live: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  consoleUrl?: string;
}> = ({
  title,
  subtitle,
  status,
  reason,
  environment,
  region,
  instanceId,
  generatedAt,
  live,
  refreshing,
  onRefresh,
  consoleUrl,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--foreground)]">{title}</h1>
            <span className="rounded-full border border-[var(--border)] bg-[var(--card)] px-2.5 py-0.5 text-xs font-semibold text-[var(--muted-foreground)]">
              {formatEnv(environment)}
            </span>
            <LivePulse status={status} live={live} />
          </div>
          {subtitle ? (
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">{subtitle}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {consoleUrl ? (
            <a
              href={consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)]/50"
            >
              Open in Console
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh platform health"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)]/50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill status={status} large />
          <p className="text-sm font-medium text-[var(--foreground)]">{reason}</p>
        </div>
        <p className="mt-2 text-xs text-[var(--muted-foreground)]">
          {instanceId ? <span className="font-mono">{instanceId}</span> : null}
          {instanceId ? ' · ' : null}
          {region}
          {generatedAt ? ` · Updated ${formatWhen(generatedAt)}` : ''}
        </p>
      </div>
    </div>
  );
};

export const HealthSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="h-8 w-64 animate-pulse rounded-lg bg-[var(--muted)]" />
    <div className="h-24 animate-pulse rounded-xl bg-[var(--muted)]" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-xl bg-[var(--muted)]" />
      ))}
    </div>
    <div className="h-64 animate-pulse rounded-xl bg-[var(--muted)]" />
  </div>
);

export const HealthError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex min-h-[16rem] items-center justify-center p-6">
    <div className="w-full max-w-md rounded-xl border-2 border-red-500 bg-[var(--card)] p-6 text-center shadow-sm">
      <p className="text-sm font-medium text-[var(--foreground)]">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90"
      >
        Retry
      </button>
    </div>
  </div>
);
